/**
 * frontend/src/utils/errorMessages.ts
 * =====================================
 * Clasificador de errores del backend → mensajes amigables para el usuario.
 *
 * Principio de diseño:
 *   Los errores del backend son strings técnicos. Este módulo los mapea a:
 *   1. Título descriptivo
 *   2. Explicación en lenguaje simple de qué salió mal
 *   3. Sugerencia concreta de qué hacer a continuación
 *
 * Para agregar un nuevo tipo de error: añadir una entrada a ERROR_PATTERNS.
 */

export interface ErrorInfo {
  /** Título corto del error (para el encabezado) */
  title: string;
  /** Explicación en lenguaje natural de qué ocurrió */
  detail: string;
  /** Sugerencia de qué hacer a continuación */
  suggestion: string;
}

// ── Patrones conocidos ────────────────────────────────────────────────────────

const ERROR_PATTERNS: Array<{ pattern: RegExp; info: ErrorInfo }> = [
  {
    pattern: /pares libro.extracto|no se encontraron pares|periodo.*no.*coincide|periodos distintos/i,
    info: {
      title: "Los archivos no corresponden al mismo período",
      detail:
        "El extracto bancario y el libro mayor deben cubrir exactamente el mismo mes y año. " +
        "El agente no encontró una correspondencia entre los archivos subidos — " +
        "es probable que correspondan a períodos distintos.",
      suggestion:
        "Verifique que ambos archivos sean del mismo mes. " +
        "Ejemplo: si el extracto bancario es de junio 2026, el libro mayor también debe ser de junio 2026.",
    },
  },
  {
    // El PDF tiene su propia entrada: el mensaje genérico de formato no
    // explica dónde conseguir el mismo extracto en un formato que sí se lee.
    pattern: /formato.*no soportado.*\.pdf|\.pdf.*no soportado/i,
    info: {
      title: "El extracto en PDF no se puede procesar",
      detail:
        "La aplicación procesa extractos en formato XLSX, XLS o CSV.",
      suggestion:
        "Descargue el extracto en Excel o CSV desde la banca en línea de su banco. " +
        "La mayoría de los bancos ofrecen esos formatos en la misma pantalla que el PDF.",
    },
  },
  {
    pattern: /xlsx.*inv[aá]lido|no se pudo leer.*excel|formato.*xlsx|excel.*corrupto|cannot read xlsx/i,
    info: {
      title: "El libro mayor (Excel) no pudo ser leído",
      detail:
        "El archivo Excel del libro mayor no tiene el formato esperado o está corrupto. " +
        "El agente espera columnas específicas en el archivo.",
      suggestion:
        "Exporte el libro mayor directamente desde su sistema contable, sin copiarlo ni " +
        "modificarlo manualmente, y vuelva a cargarlo.",
    },
  },
  {
    // Cubre los formatos que el backend rechaza por extensión, salvo el PDF,
    // que tiene su propia entrada más arriba porque necesita explicar que dejó
    // de aceptarse.
    pattern: /formato de .* no soportado|extensi[oó]n no soportada|no se puede leer un archivo/i,
    info: {
      title: "Ese formato de archivo no se puede procesar",
      detail:
        "La aplicación lee extractos y libros mayores en XLSX, XLS y CSV.",
      suggestion:
        "Vuelva a exportar el archivo desde la banca en línea o el sistema contable " +
        "en formato Excel o CSV.",
    },
  },
  {
    // El error técnico ya avisó a soporte desde el backend: acá solo se
    // acompaña al usuario, sin pedirle que haga de diagnosticador.
    pattern: /error t[eé]cnico|soporte fue notificado/i,
    info: {
      title: "Ocurrió un problema en la aplicación",
      detail:
        "El problema no se debe a una acción suya: se produjo una falla en la " +
        "aplicación y el equipo de soporte ya recibió el aviso con el detalle.",
      suggestion:
        "Intente de nuevo en unos minutos. Si el problema persiste, informe a " +
        "soporte qué archivos estaba cargando para que puedan reproducirlo.",
    },
  },
  {
    // La sesión vive en memoria: un reinicio del servidor la borra. Es el
    // caso más confuso de todos, porque el usuario no hizo nada distinto.
    pattern: /ya no est[aá] disponible|no existe sesi[oó]n|sesi[oó]n perdida/i,
    info: {
      title: "Se perdió el hilo de esta conciliación",
      detail:
        "La conciliación en curso dejó de estar disponible. Suele ocurrir cuando " +
        "el servidor se reinició o transcurrió mucho tiempo desde su inicio.",
      suggestion:
        "Vuelva a subir los archivos e inicie la conciliación nuevamente. Los " +
        "perfiles de mapeo guardados se conservan.",
    },
  },
  {
    // El libro mayor sin desplegar es el error de uso más frecuente y el que
    // más se parece a un bug: el archivo abre bien, pero no trae movimientos.
    pattern: /ning[uú]n movimiento|se encontraron .* cuentas|sin desplegar/i,
    info: {
      title: "El libro mayor no tiene los movimientos desplegados",
      detail:
        "El archivo trae las cuentas pero no los asientos de cada una, así que " +
        "no hay nada contra qué conciliar el extracto.",
      suggestion:
        "Vuelva a exportarlo desde el sistema contable con la cuenta desplegada, " +
        "de modo que cada movimiento salga en su propia fila.",
    },
  },
  {
    pattern: /timeout|tiempo.*agotado|request.*timed|timed out/i,
    info: {
      title: "El procesamiento tardó demasiado",
      detail:
        "El agente superó el tiempo límite durante el procesamiento. " +
        "Esto puede ocurrir con archivos de gran volumen o por problemas de conectividad.",
      suggestion:
        "Intente con un archivo de menor volumen (un mes con pocas transacciones). " +
        "Si el problema persiste, verifique la conexión y comuníquese con soporte.",
    },
  },
  {
    pattern: /conexi[oó]n|connection.*refused|network|ECONNREFUSED|no se pudo conectar/i,
    info: {
      title: "No se pudo conectar con el servidor",
      detail:
        "Se interrumpió la comunicación con la aplicación.",
      suggestion:
        "Verifique su conexión a la red e intente de nuevo. Si el problema persiste, comuníquese con soporte.",
    },
  },
  {
    pattern: /memoria|memory|out of memory|MemoryError/i,
    info: {
      title: "El servidor no tuvo suficiente memoria",
      detail:
        "El proceso del agente consumió más memoria de la disponible. " +
        "Suele ocurrir con archivos de extractos muy voluminosos.",
      suggestion:
        "Intente dividir el período a conciliar, por quincena en lugar del mes completo. " +
        "Si el problema persiste, comuníquese con soporte.",
    },
  },
];

// ── Clasificador ───────────────────────────────────────────────────────────────

// Lo ve cualquier error sin patrón. No menciona servidores ni registros: lo
// lee un contador, que no tiene acceso a ninguno de los dos.
const ERROR_DEFAULT: ErrorInfo = {
  title: "Ocurrió un error inesperado",
  detail:
    "La aplicación encontró un problema que no pudo identificar.",
  suggestion:
    "Intente de nuevo. Si el problema persiste, informe a soporte qué archivos " +
    "estaba cargando y en qué paso se encontraba, para que puedan reproducirlo.",
};

/**
 * Toma la lista de errores del backend y devuelve el `ErrorInfo` más
 * descriptivo encontrado. Si ningún patrón coincide, retorna el error genérico.
 */
export function classifyErrors(errores: string[]): ErrorInfo {
  const combined = errores.join(" ");

  for (const { pattern, info } of ERROR_PATTERNS) {
    if (pattern.test(combined)) return info;
  }

  return ERROR_DEFAULT;
}

/**
 * Formatea los errores raw del backend como texto plano para mostrar en detalle técnico.
 */
export function formatRawErrors(errores: string[]): string {
  if (errores.length === 0) return "Sin detalles disponibles.";
  return errores.map((e, i) => `[${i + 1}] ${e}`).join("\n");
}
